import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { match, P } from 'ts-pattern';
import type { Contest, ContestType } from '@betterpool/api-types';

import { getPromotions } from '~/domains/promotions/services/promotions.api';
import {
  PromoCarouselLocation,
  VoucherType} from '~/domains/promotions/types';
import type {
  VoucherResponseDto,
  PromotionItem
} from '~/domains/promotions/types';
import { useContest } from '~/domains/contest/hooks/useContest';

import { PromotionsQueryKeys } from '../constants/query';

export const filterContestVoucher = (voucher: VoucherResponseDto, contestData?: Contest,) => {
  // Voucher data missing - reject
  if (!voucher) return false;

  // Contest data missing - accept (i.e. contest lobby carousels don't have a single contest data)
  if (!contestData) return true;

  // EDGE-29560: hide vouchers that can't cover this contest's entry cost (both in cents)
  if (voucher.maxValue < contestData.entry_fee) return false;

  // Don't show quickPicks vouchers
  if (voucher.voucherType === VoucherType.QUICKPICKS) return false;

  if (voucher.voucherType === VoucherType.CONTEST_ID_RESTRICTED) {
    return contestData.id === voucher.restrictedContestId;
  }

  if (voucher.voucherType === VoucherType.LEAGUES) {
    let isValid = true;

    if (voucher.league !== contestData.league.name) isValid = false;
    if (voucher.contestType && voucher.contestType !== contestData.contest_type as ContestType) isValid = false;

    return isValid;
  }

  return true;
}

export const useGetPromotionsData = (location: PromoCarouselLocation, contestId?: string) => {
  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data, isLoading, isError } = useQuery({
    queryKey: PromotionsQueryKeys.PROMOTIONS(location),
    queryFn: () => getPromotions(location),
  });

  const { data: contestData } = useContest(contestId, !!contestId);

  const items: PromotionItem[] = useMemo(() => {
    const responseData = data?.data ?? [];

    return match({ location, contestId })
      // ContestDetail filtering not implemented on BE yet, so we filter on the client
      .with({ location: PromoCarouselLocation.CONTEST, contestId: P.string }, () => responseData.filter(
        (item) => filterContestVoucher(item.voucher, contestData)
      ))
      .otherwise(() => responseData);
  }, [data, location, contestId, contestData]);

  const totalCount = useMemo(() => items?.length ?? 0, [items]);

  return {
    items,
    totalCount,
    isLoading,
    isError,
  };
};
